import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { TellescopeModelFromName } from "@tellescope/tanstack-query";
import { type FreeformFieldOption, type FreeformFilterBlock } from "@tellescope/utils";
import { RELATIONSHIP_TYPES } from "@tellescope/constants";
import { BlockProps } from "../types";
import { useBlock } from ".";

export type RelatedContact = TellescopeModelFromName<"endusers">;

export interface RelatedContactWithType {
  contact: RelatedContact;
  relationshipType: string;
}

export type RelatedContactsBlockMode = "list" | "add";

export interface UseRelatedContactsBlockArgs extends BlockProps {}

export type UseRelatedContactsBlockReturn = ReturnType<typeof useRelatedContactsBlock>;

export const useRelatedContactsBlock = (args: UseRelatedContactsBlockArgs) => {
  const { t } = useTranslation("contacts");
  const [mode, setMode] = useState<RelatedContactsBlockMode>("list");

  const block = useBlock({
    ...args,
    type: "related-contacts.related-contacts",
    select: (enduser) => enduser.relationships ?? [],
  });

  const relationships = useMemo(() => block.enduserQuery.data ?? [], [block.enduserQuery.data]);
  const relationshipIds = useMemo(() => relationships.map((rel) => rel.id), [relationships]);

  const filterBlock = useMemo<FreeformFilterBlock>(
    () =>
      block.parsedInfo?.filter ?? {
        id: args.blockId ?? `${args.viewId}-${args.blockIndex}`,
        logic: "and",
        filters: [],
      },
    [block.parsedInfo?.filter, args.blockId, args.viewId, args.blockIndex]
  );

  const filterFieldOptions = useMemo<FreeformFieldOption[]>(
    () => [
      {
        value: "type",
        label: t("Relationship"),
        valueType: "tags",
        operations: ["equals", "does_not_equal"],
      },
    ],
    [t]
  );

  const relationshipSelectOptions = useMemo<string[]>(() => {
    // Include any type already chosen in the filter so the MultiSelect can render it as a badge.
    const selected = filterBlock.filters.flatMap((filter) => (Array.isArray(filter.value) ? filter.value : []));
    return [...new Set<string>([...RELATIONSHIP_TYPES, ...selected])];
  }, [filterBlock.filters]);

  const onFilterChange = useCallback(
    (nextBlock: FreeformFilterBlock) => {
      block.updateInfo({ ...block.parsedInfo, id: block.parsedInfo?.id ?? crypto.randomUUID(), filter: nextBlock });
    },
    [block]
  );

  const openAddView = useCallback(() => setMode("add"), []);
  const closeAddView = useCallback(() => setMode("list"), []);

  return {
    ...block,
    mode,
    openAddView,
    closeAddView,
    enduserId: args.enduserId,
    relationships,
    relationshipIds,
    filterBlock,
    filterFieldOptions,
    relationshipSelectOptions,
    onFilterChange,
  };
};
